import { Controller } from "@hotwired/stimulus"

// 카카오 우편번호 서비스 스크립트 (2026 규격 — kakao.Postcode 네임스페이스)
const KAKAO_POSTCODE_SRC = "https://t1.kakaocdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js"
let kakaoPostcodePromise = null

// 사업장 등록 폼 컨트롤러
export default class extends Controller {
  static targets = [
    "businessNumber", "businessNumberValid", "businessNumberInvalid",
    "businessNumberCheckMessage", "businessNumberCheckButton",
    "phone", "phoneValid", "phoneInvalid",
    "workplaceName", "representativeName",
    "industryMajor", "industryMinor", "industryMinorText",
    "employeeCount",
    "zipCode", "address", "addressDetail",
    "sido", "sigungu",
    "submitButton",
    "successAlert", "successMessage",
    "errorAlert", "errorMessage"
  ]

  connect() {
    this.isBusinessNumberVerified = false
    this.isSubmitting = false
    this.validateForm()
    this.setupDropdownListeners()
  }

  // 사업자 등록번호 중복확인 상태 업데이트 (business-number-validation 컨트롤러에서 호출)
  updateBusinessNumberStatus(isAvailable) {
    this.isBusinessNumberVerified = isAvailable
    this.validateForm()
  }

  // Fomantic UI dropdown 변경 이벤트 리스너 설정
  setupDropdownListeners() {
    // jQuery와 Fomantic UI가 로드될 때까지 대기
    const checkAndSetup = () => {
      if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
        // 모든 dropdown에 onChange 콜백 추가
        $(this.element).find('.ui.dropdown').each((_, dropdown) => {
          $(dropdown).dropdown('setting', 'onChange', () => {
            this.validateForm()
          })
        })
      } else {
        setTimeout(checkAndSetup, 100)
      }
    }
    checkAndSetup()
  }

  // 사업자등록번호 유효성 검사 (XXX-XX-XXXXX 형식)
  isBusinessNumberValid(value) {
    return /^\d{3}-\d{2}-\d{5}$/.test(value)
  }

  // 전화번호 유효성 검사 (0XX-XXX-XXXX 또는 0XX-XXXX-XXXX 형식)
  isPhoneValid(value) {
    return /^\d{2,3}-\d{3,4}-\d{4}$/.test(value)
  }

  // 필드 유효성 검사 (입력 시마다 호출)
  validateField(event) {
    const target = event.target
    const name = target.name

    // 입력 중에는 검증 메시지 숨기기
    if (name.includes('phone')) {
      this.hidePhoneMessages()
    }

    this.validateForm()
  }

  // blur 시 전화번호 자동 포맷팅 및 형식 검증
  validatePhoneOnBlur() {
    if (!this.hasPhoneTarget) return

    let phone = this.phoneTarget.value.trim()

    // '-'가 없으면 자동으로 포맷팅
    if (phone.length > 0 && !phone.includes('-')) {
      phone = this.formatPhoneNumber(phone)
      this.phoneTarget.value = phone
    }

    const isValid = this.isPhoneValid(phone)

    if (phone.length > 0) {
      if (isValid) {
        this.phoneValidTarget.classList.remove('hidden')
        this.phoneValidTarget.style.display = 'block'
        this.phoneInvalidTarget.classList.add('hidden')
        this.phoneInvalidTarget.style.display = 'none'
      } else {
        this.phoneValidTarget.classList.add('hidden')
        this.phoneValidTarget.style.display = 'none'
        this.phoneInvalidTarget.classList.remove('hidden')
        this.phoneInvalidTarget.style.display = 'block'
      }
    } else {
      this.hidePhoneMessages()
    }

    this.validateForm()
  }

  // 전화번호 자동 포맷팅 (숫자만 있을 때 '-' 삽입)
  formatPhoneNumber(value) {
    // 숫자만 추출
    const digits = value.replace(/[^0-9]/g, '')

    // 서울 지역번호 (02)
    if (digits.startsWith('02')) {
      if (digits.length === 9) {
        // 02-XXX-XXXX
        return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`
      } else if (digits.length === 10) {
        // 02-XXXX-XXXX
        return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`
      }
    }
    // 휴대폰 또는 기타 지역번호 (3자리)
    else if (digits.length === 10) {
      // 0XX-XXX-XXXX
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
    } else if (digits.length === 11) {
      // 0XX-XXXX-XXXX
      return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`
    }

    // 포맷팅할 수 없으면 원본 반환
    return value
  }

  hidePhoneMessages() {
    if (this.hasPhoneValidTarget) {
      this.phoneValidTarget.classList.add('hidden')
      this.phoneValidTarget.style.display = 'none'
    }
    if (this.hasPhoneInvalidTarget) {
      this.phoneInvalidTarget.classList.add('hidden')
      this.phoneInvalidTarget.style.display = 'none'
    }
  }

  // 폼 전체 유효성 검사 — 버튼은 항상 활성, 강조는 [사업장 등록] 클릭 시점에만.
  // 시그니처는 다른 핸들러 호출처와의 호환을 위해 유지하되 내부는 no-op.
  validateForm() {}

  // 산업분류 대분류 값 가져오기
  getIndustryMajorValue() {
    if (this.hasIndustryMajorTarget) {
      return this.industryMajorTarget.value || ''
    }
    return ''
  }

  // 산업분류 중분류 값 가져오기 (dropdown 또는 text input)
  getIndustryMinorValue() {
    const majorValue = this.getIndustryMajorValue()

    // 대분류가 '기타'인 경우 text input 값 사용
    if (majorValue === '기타') {
      if (this.hasIndustryMinorTextTarget) {
        return this.industryMinorTextTarget.value.trim() || ''
      }
      return ''
    }

    // 일반 대분류인 경우 dropdown 값 사용
    if (this.hasIndustryMinorTarget) {
      return this.industryMinorTarget.value || ''
    }
    return ''
  }

  getEmployeeCountValue() {
    const input = document.querySelector('input[name="company[employee_count]"]')
    return input ? input.value : ''
  }

  // 주소 검색 — 카카오 우편번호 서비스 팝업
  async searchAddress() {
    try {
      await this.loadKakaoPostcode()
    } catch (e) {
      alert('주소 검색 서비스를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.')
      return
    }

    // 2026 규격: kakao.Postcode 네임스페이스 (구 daum.Postcode 는 폴백)
    const Postcode = window.kakao?.Postcode || window.daum?.Postcode
    if (!Postcode) {
      alert('주소 검색 서비스를 사용할 수 없습니다.')
      return
    }

    new Postcode({
      oncomplete: (data) => {
        // 우편번호 + 기본 주소 (사용자가 선택한 타입: R=도로명 / J=지번)
        this.zipCodeTarget.value = data.zonecode
        this.addressTarget.value = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress

        // B2B 지역 통계용 — 시/도, 시/군/구 hidden 필드
        if (this.hasSidoTarget) this.sidoTarget.value = data.sido
        if (this.hasSigunguTarget) this.sigunguTarget.value = data.sigungu

        this.validateForm()

        // 상세 주소 입력으로 커서 이동
        this.addressDetailTarget.focus()
      }
    }).open()
  }

  // 카카오 우편번호 스크립트 lazy-load (1회만, 실패 시 재시도 가능)
  loadKakaoPostcode() {
    if (window.kakao?.Postcode || window.daum?.Postcode) return Promise.resolve()
    if (kakaoPostcodePromise) return kakaoPostcodePromise

    kakaoPostcodePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = KAKAO_POSTCODE_SRC
      script.onload = () => resolve()
      script.onerror = () => {
        kakaoPostcodePromise = null // 다음 클릭에서 재시도
        reject(new Error('kakao postcode script load failed'))
      }
      document.head.appendChild(script)
    })
    return kakaoPostcodePromise
  }

  // 취소 버튼
  handleCancel() {
    // 이전 페이지로 이동
    window.history.back()
  }

  // 폼 제출 — 빈 필수 항목 강조 + 사업자등록번호 미인증 강조를 한꺼번에 적용 후,
  // 누락 있으면 첫 항목으로 스크롤/포커스 + 차단. 누락 없으면 fetch 진행.
  async handleSubmit(event) {
    event.preventDefault()

    if (this.isSubmitting) return

    let firstMissing = null

    // 1) 빈 필수 항목 강조 — text input. 사업자등록번호는 형식/중복확인 상태에 따라
    // 별도 처리 (값 있고 미인증 시에는 input 강조 X, 버튼 + 안내 박스만).
    const textChecks = [
      this.workplaceNameTarget,
      // 대표자명은 오랫동안 이 목록에도, collectFormData 에도 없었다. 입력칸은 화면에
      // 있는데 전송에서 빠져, **채워 넣어도 서버가 "입력해 주세요" 로 거부**했다
      // (2026-08-21). 화면에서 막지도 못하니 사용자는 빠져나갈 길이 없었다.
      this.representativeNameTarget,
      this.phoneTarget,
      this.zipCodeTarget,
      this.addressTarget
    ]
    textChecks.forEach((el) => {
      if (!el) return
      if (el.value.trim().length === 0) {
        this.markInvalid(el)
        firstMissing ||= el
      }
    })

    // 2) dropdown / select 누락 강조 — wrapper 에 클래스 토글.
    //
    // ⚠️ 표준산업분류(대분류·중분류)는 **여기서 검사하지 않는다 — 선택 항목이다.**
    // 폼 라벨에도 필수 표시(*)가 없고, 서버도 요구하지 않는다: workplaces.industry 는
    // varchar[] 기본값 '{}' 이고 Workplace 에 관련 검증이 없다.
    //
    // 예전에는 이 자리에서 둘 다 막았다. handleSubmit 첫 줄이 preventDefault 라
    // **요청이 서버까지 가지도 않아**, 화면은 선택이라고 말하는데 등록이 안 되는
    // 상태였다(2026-08-24 사용자 보고). 아래 대표자명 주석과 방향만 반대일 뿐
    // 같은 부류다 — 화면과 검증이 어긋나면 사용자에게는 빠져나갈 길이 없다.
    if (this.getEmployeeCountValue().length === 0 && this.hasEmployeeCountTarget) {
      this.markDropdownInvalid(this.employeeCountTarget)
      firstMissing ||= this.employeeCountTarget
    }

    // 4) 사업자등록번호 — 빈 값 / 값은 있으나 미인증 두 케이스로 분기.
    //    값은 있는데 미인증 (형식 OK 인 상태 포함) 일 때는 input 빨간 강조 없이
    //    버튼 + 안내 박스로만 누락 안내 (input 의 시각 상태는
    //    business_number_validation_controller 가 형식/사용가능/중복에 따라 관리).
    if (this.businessNumberTarget.value.trim().length === 0) {
      this.markInvalid(this.businessNumberTarget)
      firstMissing ||= this.businessNumberTarget
      this.hideBusinessNumberCheckMessage()
    } else if (!this.isBusinessNumberVerified) {
      if (this.hasBusinessNumberCheckButtonTarget) {
        this.markInvalid(this.businessNumberCheckButtonTarget)
      }
      this.showBusinessNumberCheckMessage()
      firstMissing ||= this.businessNumberTarget
    } else {
      this.hideBusinessNumberCheckMessage()
    }

    if (firstMissing) {
      firstMissing.scrollIntoView({ behavior: 'smooth', block: 'center' })
      if (typeof firstMissing.focus === 'function') firstMissing.focus()
      return
    }

    this.isSubmitting = true
    this.showLoadingState()

    try {
      const formData = this.collectFormData()
      const response = await fetch('/company/registration', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: JSON.stringify({ company: formData })
      })

      const data = await response.json()

      if (data.success) {
        this.showAlert(true, '기업등록에 성공했습니다. 대시보드로 이동합니다.')
      } else {
        this.showAlert(false, data.error?.message || '기업 등록에 실패했습니다.')
      }
    } catch (error) {
      console.error('기업 등록 오류:', error)
      this.showAlert(false, '기업 등록 중 오류가 발생했습니다. 다시 시도해 주세요.')
    } finally {
      this.isSubmitting = false
      this.hideLoadingState()
    }
  }

  // 폼 데이터 수집
  collectFormData() {
    return {
      business_number: this.businessNumberTarget.value,
      name: this.workplaceNameTarget.value,
      representative_name: this.representativeNameTarget.value,
      phone: this.phoneTarget.value,
      industry: [this.getIndustryMajorValue(), this.getIndustryMinorValue()],
      employee_count: this.getEmployeeCountValue(),
      zip_code: this.zipCodeTarget.value,
      address1: this.addressTarget.value,
      address2: this.addressDetailTarget.value,
      // 주소 검색이 채워 넣는 값(지역 통계용). 여기 빠져 있어서 회사 30곳이 전부
      // 비어 있었다 — 대표자명과 같은 종류의 누락이다(2026-08-21).
      sido: this.hasSidoTarget ? this.sidoTarget.value : '',
      sigungu: this.hasSigunguTarget ? this.sigunguTarget.value : ''
    }
  }

  // CSRF 토큰 가져오기
  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  // 로딩 상태 표시
  showLoadingState() {
    this.submitButtonTarget.disabled = true
    const buttonText = this.submitButtonTarget.querySelector('span') || this.submitButtonTarget
    this.originalButtonText = buttonText.textContent
    buttonText.textContent = '등록 중...'
  }

  // 로딩 상태 해제
  hideLoadingState() {
    this.submitButtonTarget.disabled = false
    const buttonText = this.submitButtonTarget.querySelector('span') || this.submitButtonTarget
    buttonText.textContent = this.originalButtonText || '사업장 등록'
    this.validateForm()
  }

  // 사업자 등록번호 중복확인 미완료 메시지 표시 / 해제.
  showBusinessNumberCheckMessage() {
    if (this.hasBusinessNumberCheckMessageTarget) {
      this.businessNumberCheckMessageTarget.classList.remove('hidden')
    }
  }

  hideBusinessNumberCheckMessage() {
    if (this.hasBusinessNumberCheckMessageTarget) {
      this.businessNumberCheckMessageTarget.classList.add('hidden')
    }
  }

  // ─── 누락 강조 헬퍼 ────────────────────────────────────────────────

  // text input / button 누락 강조.
  // input 은 focus/hover variant 가 강조 색을 덮지 않도록 !important variant 사용.
  markInvalid(el) {
    if (!el) return
    if (el.tagName === 'BUTTON') {
      if (el.dataset.originalBorderColor === undefined) {
        el.dataset.originalBorderColor = el.style.borderColor || ''
      }
      el.style.borderColor = 'var(--danger-500)'
    } else {
      el.classList.add('!border-[var(--danger-500)]', '!bg-[var(--danger-50)]')
    }
  }

  _restoreBorder(el) {
    if (!el) return
    if (el.tagName === 'BUTTON') {
      if (el.dataset.originalBorderColor !== undefined) {
        el.style.borderColor = el.dataset.originalBorderColor
        delete el.dataset.originalBorderColor
      }
    } else {
      el.classList.remove('!border-[var(--danger-500)]', '!bg-[var(--danger-50)]')
    }
  }

  // dropdown / select 의 hidden input 에서 가까운 .ui.selection.dropdown wrapper 에 클래스 토글.
  // Fomantic UI 의 .ui.selection.dropdown self-style 이 specificity 가 더 높아
  // Tailwind 단일 클래스로는 덮이지 않음 — !important variant 필수.
  _dropdownWrapper(hiddenInput) {
    return hiddenInput ? hiddenInput.closest('.ui.selection.dropdown') : null
  }

  markDropdownInvalid(hiddenInput) {
    const wrap = this._dropdownWrapper(hiddenInput)
    if (!wrap) return
    wrap.classList.add('!border-[var(--danger-500)]', '!bg-[var(--danger-50)]')
  }

  _restoreDropdownInvalid(hiddenInput) {
    const wrap = this._dropdownWrapper(hiddenInput)
    if (!wrap) return
    wrap.classList.remove('!border-[var(--danger-500)]', '!bg-[var(--danger-50)]')
  }

  // text input 의 input 이벤트 핸들러.
  clearInvalid(event) {
    this._restoreBorder(event.currentTarget)
  }

  // dropdown / select 의 change 이벤트 핸들러.
  clearDropdownInvalid(event) {
    this._restoreDropdownInvalid(event.currentTarget)
  }

  // 사업자등록번호 input + [중복확인] 버튼 + 안내 박스 일괄 해제.
  // input 의 input 이벤트 또는 [중복확인] 버튼 click 시 호출.
  clearBusinessNumberGroup() {
    if (this.hasBusinessNumberTarget) {
      this._restoreBorder(this.businessNumberTarget)
    }
    if (this.hasBusinessNumberCheckButtonTarget) {
      this._restoreBorder(this.businessNumberCheckButtonTarget)
    }
    this.hideBusinessNumberCheckMessage()
  }

  // Alert 표시
  showAlert(success, message) {
    if (success) {
      // 성공 메시지 업데이트 및 Alert 열기
      if (this.hasSuccessMessageTarget) {
        this.successMessageTarget.textContent = message
      }
      // Stimulus alert controller의 open 메소드 호출
      const alertController = this.application.getControllerForElementAndIdentifier(
        this.successAlertTarget,
        'alert'
      )
      if (alertController) {
        alertController.open()
      }
    } else {
      // 에러 메시지 업데이트 및 Alert 열기
      if (this.hasErrorMessageTarget) {
        this.errorMessageTarget.textContent = message
      }
      const alertController = this.application.getControllerForElementAndIdentifier(
        this.errorAlertTarget,
        'alert'
      )
      if (alertController) {
        alertController.open()
      }
    }
  }

  // 성공 Alert 닫힘 처리
  handleSuccessAlertClose() {
    // 대시보드 페이지로 이동
    window.location.href = '/workplace/dashboard'
  }

  // 에러 Alert 닫힘 처리
  handleErrorAlertClose() {
    // 에러 Alert 닫힘 시 특별한 처리 없음
  }
}
